import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Gift, Copy, Check, Share2, Loader2, Users } from 'lucide-react';

/**
 * ReferralCard — shows the coach's personal referral code + share link.
 * Every successful signup with this code grants the coach +30 days of comp'd premium,
 * and the new user +7 bonus trial days.
 */
export default function ReferralCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/me/referral-stats');
        if (!cancelled) {
          // Adapt new response shape → legacy fields the component already uses
          const d = r.data || {};
          setData({
            code: d.referral_code,
            share_url: d.share_link,
            redemptions: d.rewards_granted || 0,
            invited_count: d.invited_count || 0,
            paid_count: d.paid_count || 0,
            days_earned: d.days_earned || 0,
          });
        }
      } catch {
        // silent — card will just hide if referrals endpoint fails
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="border-0 shadow-md" data-testid="referral-card-loading">
        <CardContent className="p-4 flex items-center gap-2 text-stone-500 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading referral code…
        </CardContent>
      </Card>
    );
  }

  if (!data?.code) return null;

  const shareUrl = data.share_url || `${window.location.origin}/?ref=${data.code}`;
  const shareText = `I'm using Diamond Scorer to score our games + keep parents in the loop. Use my link and you get a 30-day free trial (way better than SportsEngine for game day): ${shareUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied!');
    } catch {
      toast.error('Could not copy');
    }
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Diamond Scorer', text: shareText, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success('Message copied — paste it anywhere!');
      }
    } catch {}
  };

  return (
    <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-amber-100" data-testid="referral-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
            <Gift className="text-white" size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-amber-900 text-base leading-tight" data-testid="referral-card-title">
              Give 30 days, get 30 days
            </h3>
            <p className="text-[12px] text-amber-800/80 leading-snug mt-0.5">
              Share your code with another coach. They get a <b>30-day trial</b> (instead of 7). When they pay, <b>you get +30 days free</b>.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-white rounded-lg border border-amber-300 px-3 py-2">
          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Your code</span>
          <span
            className="font-mono font-black text-xl text-amber-900 tracking-widest flex-1 text-center"
            data-testid="referral-code-display"
          >
            {data.code}
          </span>
          <span
            className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full whitespace-nowrap"
            data-testid="referral-redemptions-count"
          >
            {data.redemptions || 0} used
          </span>
        </div>

        <div className="flex gap-2">
          <Input
            readOnly
            value={shareUrl}
            className="font-mono text-[11px] bg-white border-amber-200 flex-1"
            data-testid="referral-link-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={copy}
            className="shrink-0 border-amber-600 text-amber-800 hover:bg-amber-100"
            data-testid="referral-copy-btn"
          >
            {copied ? <><Check size={14} className="mr-1" /> Copied</> : <><Copy size={14} className="mr-1" /> Copy</>}
          </Button>
        </div>

        <Button
          className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold"
          onClick={share}
          data-testid="referral-share-btn"
        >
          <Share2 size={14} className="mr-2" /> Share with a coach friend
        </Button>

        {(data.redemptions > 0 || data.invited_count > 0) && (
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold justify-center" data-testid="referral-rewards-banner">
            <Users size={12} />
            {data.redemptions > 0
              ? `You've earned ${data.days_earned || data.redemptions * 30} free premium days!`
              : `${data.invited_count} invited · waiting for first payment to credit you 30 days.`}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
